/**
 * What a brand says matters in its footage, and how the analyser reads it. Primary moments each earn
 * their own highlight at the top of the pile; secondary moments are indexed at lower confidence and
 * repeated secondary steps that look alike fold into one scene. Pure matching: a segment's words
 * against each line, by stem, so "hands massaging" finds "stylist massages the scalp".
 */
import { z } from 'zod';
export const PRIORITY_LINE_MAX_CHARS = 120;
export const PRIORITY_LINES_MAX = 12;
/** The brand's free-text direction to the analyser, passed on verbatim. */
export const DIRECTION_MAX_CHARS = 1500;
export const FootageKey = z.enum(['reel', 'longform', 'mixed', 'custom']);
/** Typical footage presets: they tell the analyser whether to cut by eye or by ear. */
export const FOOTAGE_KINDS = [
    { key: 'reel', label: 'Short-form reels', minS: 15, maxS: 90, hint: '15–90 s · cut where the picture changes; every moment is a candidate' },
    { key: 'longform', label: 'Talking to camera', minS: 120, maxS: 1200, hint: '2–20 min · interviews and explainers; passages break at pauses, not pixels' },
    { key: 'mixed', label: 'Mixed', minS: 15, maxS: 1200, hint: 'The clip\'s own length decides: reels cut by eye, long takes by ear' },
];
/**
 * What a brand may say, and all it may say: the nature of its own footage. How scenes split and join
 * (a turn to camera breaks a scene, one described action reconnects across a cut, look-alike secondary
 * steps fold, long takes become passages) is global, the same for a dessert brand and a salon, and
 * lives in code and the neutral prompt, never here.
 */
export const AnalysePriorities = z.object({
    /** Whatever the brand wants to say to the analyser in its own words; carried into the prompt as written. */
    direction: z.string().max(DIRECTION_MAX_CHARS).default(''),
    /** Each line one moment the brand always wants as its own highlight, most important first. */
    primaryShots: z.array(z.string().min(1).max(PRIORITY_LINE_MAX_CHARS)).max(PRIORITY_LINES_MAX).default([]),
    /** Moments worth indexing but not leading with; repeated secondary steps may share one scene. */
    secondaryShots: z.array(z.string().min(1).max(PRIORITY_LINE_MAX_CHARS)).max(PRIORITY_LINES_MAX).default([]),
    footageKey: FootageKey.nullable().default(null),
    footageMinS: z.number().int().min(1).max(7200).nullable().default(null),
    footageMaxS: z.number().int().min(2).max(7200).nullable().default(null),
});
const STOP = new Set(['the', 'a', 'an', 'of', 'and', 'or', 'with', 'for', 'to', 'in', 'on', 'at', 'as', 'one', 'clip', 'shot', 'shots', 'close', 'up', 'camera', 'together', 'single', 'separate', 'two', 'steps', 'step', 'such']);
/** A crude English stem: enough for "dragging" to meet "drags" and "ingredients" to meet "ingredient". */
export function stem(word) {
    let w = word.toLowerCase().replace(/[^a-z]/g, '');
    if (w.length <= 3)
        return w;
    for (const suffix of ['ings', 'ing', 'ion', 'ed', 'es', 's']) {
        if (w.endsWith(suffix) && w.length - suffix.length >= 3) {
            w = w.slice(0, -suffix.length);
            break;
        }
    }
    // "dragging" -> "dragg" -> "drag"; "taste" and "tasting" both -> "tast".
    if (w.length >= 4 && w[w.length - 1] === w[w.length - 2] && !'lsz'.includes(w[w.length - 1]))
        w = w.slice(0, -1);
    if (w.length >= 4 && w.endsWith('e'))
        w = w.slice(0, -1);
    return w;
}
function stems(text) {
    return new Set(text.split(/[^a-zA-Z]+/).filter((t) => t && !STOP.has(t.toLowerCase())).map(stem).filter((t) => t.length >= 3));
}
/** A stem present in at least this share of a clip's segments says nothing about any one of them. */
export const COMMON_STEM_SHARE = 0.4;
/** And in at least this many segments, so a short clip's coincidence is not a theme. */
const COMMON_STEM_MIN_COUNT = 3;
/**
 * Stems that run through a whole clip ("pudding" in a pudding brand's every title) carry no
 * discriminating power for one segment against another; they are ignored when matching lines.
 */
export function commonStems(texts) {
    const out = new Set();
    const counts = new Map();
    for (const t of texts)
        for (const w of stems(t))
            counts.set(w, (counts.get(w) ?? 0) + 1);
    for (const [w, n] of counts)
        if (n >= COMMON_STEM_MIN_COUNT && n / texts.length >= COMMON_STEM_SHARE)
            out.add(w);
    return out;
}
/**
 * How well a segment's words match one priority line: 0 unless at least two of the line's content stems
 * appear (all of them for a one-word line), else the share found. One shared word never promotes a
 * moment: "spoon" in shot is not "spoon dragging", "product" is not "the finished product".
 */
export function matchScore(segmentText, line, headline = '', ignore = new Set()) {
    const want = new Set([...stems(line)].filter((w) => !ignore.has(w)));
    if (!want.size)
        return 0;
    const have = stems(`${headline} ${segmentText}`);
    let hit = 0;
    for (const w of want)
        if (have.has(w))
            hit++;
    return hit >= Math.min(2, want.size) ? hit / want.size : 0;
}
/** The class a segment falls in and the line it matched; a primary line wins a tie. */
export function classifyByPriorities(segment, p, ignore = new Set()) {
    const words = typeof segment === 'string' ? { headline: '', text: segment } : segment;
    let best = { cls: 'other', line: null, score: 0 };
    for (const [cls, lines] of [['primary', p.primaryShots], ['secondary', p.secondaryShots]]) {
        for (const line of lines) {
            const score = matchScore(words.text, line, words.headline, ignore);
            if (score > 0 && (score > best.score || (score === best.score && best.cls !== 'primary')))
                best = { cls, line, score };
        }
    }
    return best;
}
//# sourceMappingURL=priorities.js.map